import { useState } from "react";
import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import SEOHead from "../components/shared/SEOHead";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import { toast } from "sonner";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Phone, Mail, MapPin, Clock, Check, Loader2 } from "lucide-react";

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await api.post("/contact", formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20">
          <div className="container-max px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-20 h-20 bg-[#16A34A]/10 mx-auto flex items-center justify-center mb-6">
                <Check className="w-10 h-10 text-[#16A34A]" />
              </div>
              <h1 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
                Message Sent
              </h1>
              <p className="text-slate-600">
                Thank you for contacting us. We'll get back to you within 24 hours.
              </p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Contact Us | Get in Touch | Hitech Transit Cardiff"
        description="Contact Hitech Transit for removals in Cardiff and South Wales. Call 07346 209593, email hello@hitechtransit.com, or fill in our contact form. We respond within 2 hours."
        canonical="/contact"
      />
      <Header />
      
      <main className="pt-20">
        <Breadcrumbs items={[{ label: "Contact", href: "/contact" }]} />
        {/* Hero */}
        <section className="bg-[#0F172A] py-20">
          <div className="container-max px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-4 block">
                Contact Us
              </span>
              <h1 className="text-4xl md:text-5xl font-['Outfit'] font-bold text-white tracking-tight mb-6">
                Get In Touch
              </h1>
              <p className="text-slate-300 text-lg">
                Have a question or need a quote? We're here to help. Reach out by phone, 
                email, or fill in the form below.
              </p>
            </div>
          </div>
        </section>

        <section className="section-padding">
          <div className="container-max">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A] mb-8">
                  Contact Information
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF5A1F]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#FF5A1F]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#0F172A]">Phone</div>
                      <a href="tel:07346209593" className="text-slate-600 hover:text-[#FF5A1F]">
                        07346 209593
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF5A1F]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#FF5A1F]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#0F172A]">Email</div>
                      <a href="mailto:hello@hitechtransit.com" className="text-slate-600 hover:text-[#FF5A1F]">
                        hello@hitechtransit.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF5A1F]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#FF5A1F]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#0F172A]">Locations</div>
                      <div className="text-slate-600">
                        Cardiff, Wales<br />
                        London, England
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF5A1F]/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#FF5A1F]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#0F172A]">Office Hours</div>
                      <div className="text-slate-600">
                        Monday - Friday: 8am - 6pm<br />
                        Saturday: 9am - 4pm<br />
                        Sunday: Closed
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="card-surface p-8">
                <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A] mb-6">
                  Send Us a Message
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="label-text">Name *</Label>
                      <Input
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="input-field"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label className="label-text">Phone</Label>
                      <Input
                        type="tel"
                        placeholder="Your phone"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="input-field"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="label-text">Email *</Label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="input-field"
                      data-testid="contact-email"
                    />
                  </div>

                  <div>
                    <Label className="label-text">Subject</Label>
                    <Input
                      placeholder="What's this about?"
                      value={formData.subject}
                      onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                      className="input-field"
                      data-testid="contact-subject"
                    />
                  </div>

                  <div>
                    <Label className="label-text">Message *</Label>
                    <Textarea
                      placeholder="Your message..."
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="min-h-[150px]"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={loading}
                    className="w-full btn-primary h-14 text-lg"
                    data-testid="contact-submit"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Message"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
